import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { getDashboardData, getMonthlyLoadSummary } from 'apis/adminServices/dashboardService';
import { Link } from 'react-router-dom';
import { useUserStore } from 'store/userStore';



const AdminDashboard: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [monthlyData, setMonthlyData] = useState<any>([]);
  const { username, profilePictureUrl } = useUserStore(state => state);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getDashboardData();
        setDashboardData(data);
        const currentYear = new Date().getFullYear(); // Get current year dynamically
        const monthlyLoadData = await getMonthlyLoadSummary(currentYear); // Pass year here

        const rawMonthly = Array.isArray(monthlyLoadData)
          ? monthlyLoadData
          : (monthlyLoadData?.data || []);

        const formattedData = rawMonthly.map((item: any) => ({
          month: item.month ? item.month.substring(0, 3) : '',
          Posted: item.loadsPosted ?? 0,
          Answered: item.loadsAnswered ?? 0,
          Unanswered: item.loadsUnanswered ?? Math.max(0, (item.loadsPosted ?? 0) - (item.loadsAnswered ?? 0)),
        }));

        setMonthlyData(formattedData);
      } catch (err) {
        console.error('Error loading dashboard data', err);
      }
    };

    fetchData();
  }, []);

  // Pie chart data
  const loadDistributionData = [
    { name: 'Answered', value: dashboardData?.loadAnsweredCount || 0, color: 'rgb(95 210 228 / 1)' },
    { name: 'Unanswered', value: dashboardData?.loadUnansweredCount || 0, color: 'rgb(228 157 95 / 1)' },
  ];

  const renderCustomizedLabel = (props: any) => {
    const RADIAN = Math.PI / 180;
    const { cx, cy, midAngle, outerRadius, payload } = props;

    if (!payload.value || payload.value === 0) return null;

    // Calculate the position for the external label
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);

    // Position the external label just outside the (smaller) pie.
    const labelRadius = outerRadius * 1.15;
    const x = cx + labelRadius * cos;
    const y = cy + labelRadius * sin;

    // Position for the connector dot - start further inside the pie
    const dotX = cx + outerRadius * 0.7 * cos;
    const dotY = cy + outerRadius * 0.7 * sin;

    // Determine box position based on which half of the pie
    const isRightSide = cos > 0;
    const boxWidth = 84;
    const boxHeight = 46;

    // Clamp the label box inside the chart's SVG (width = 2*cx, height = 2*cy)
    // so it's never clipped by the narrow container edge, whatever the angle.
    let boxX = isRightSide ? x : x - boxWidth;
    boxX = Math.max(4, Math.min(boxX, cx * 2 - boxWidth - 4));
    const boxY = Math.max(4, Math.min(y - boxHeight / 2, cy * 2 - boxHeight - 4));

    return (
      <g>
        {/* Connector line */}
        <line
          x1={dotX}
          y1={dotY}
          x2={x}
          y2={y}
          stroke="#000"
          strokeWidth={1}
        />

        {/* Connector dot */}
        <circle
          cx={dotX}
          cy={dotY}
          r={4}
          fill="#000"
        />

        {/* Label box with content inside */}
        <foreignObject
          x={boxX}
          y={boxY}
          width={boxWidth}
          height={boxHeight}
        >
          <div
            style={{
              backgroundColor: '#1A1C31',
              color: 'white',
              borderRadius: '8px',
              padding: '6px 8px',
              fontSize: '14px',
              fontWeight: 'bold',
              textAlign: 'center',
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              boxSizing: 'border-box'
            }}
          >
            <div style={{
              fontSize: '12px',
              fontWeight: '100', opacity: '0.85'
            }}>{payload.name}</div>
            <div style={{ fontSize: '16px', fontWeight: '600' }}>
              {payload.value.toLocaleString()}
            </div>
          </div>
        </foreignObject>
      </g>
    );
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-150 rounded-lg shadow-lg text-xs space-y-1.5 min-w-[130px]">
          <p className="font-semibold text-grease-100 mb-1">{label}</p>
          {payload.map((entry: any, index: number) => (
            <div key={`tooltip-item-${index}`} className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-1.5">
                <span
                  className="w-2.5 h-2.5 rounded-full inline-block shrink-0"
                  style={{ backgroundColor: entry.color || entry.fill }}
                />
                <span className="text-gray-600">{entry.name} :</span>
              </div>
              <span className="font-semibold text-grease-100">
                {Number(entry.value).toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="-m-6 bg-[#f1f3f6] font-poppins">
      <div className="px-6 pt-6">
        <section className="bg-white p-4 rounded-xl flex items-center gap-4">
          <img
            className="h-14 w-14 rounded-full object-cover"
            src={profilePictureUrl || "/images/avatar.png"}
            alt=""
            onError={(e) => {
              e.currentTarget.src = "/images/avatar.png";
            }}
          />
          <div>
            <p className="text-primaryblue-100 text-xs">Welcome Back!</p>
            <p className="mt-1 text-primaryblue-100 text-xl font-bold">
              {username ? `${username.charAt(0).toUpperCase()}${username.slice(1)}` : 'UserName'}
            </p>

          </div>
        </section>

        <section className="grid md:grid-cols-12 gap-6 mt-6">
          {/* Cards go 3-across only at xl — inside md:col-span-8 a 3-column row
              at tablet width leaves each card too narrow for its label. */}
          <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
            {/* h-full on both the link and the card so all three stay the same
                height whatever their label/value lengths. shrink-0 keeps the
                icon and arrow at full size and lets only the text column give. */}
            {[
              {
                to: "/admin/load-posted",
                title: "Total Loads Posted",
                count: dashboardData?.loadPostedCount ?? 0,
                bg: "bg-violet-100",
              },
              {
                to: "/admin/load-answered",
                title: "Total Loads Answered",
                count: dashboardData?.loadAnsweredCount ?? 0,
                bg: "bg-skyblue-100",
              },
              {
                to: "/admin/load-unanswered",
                title: "Total Loads Unanswered",
                count: dashboardData?.loadUnansweredCount ?? 0,
                bg: "bg-brown-100",
              },
            ].map((card) => (
              <Link key={card.to} to={card.to} className="block h-full">
                <div className="bg-white p-4 rounded-xl flex items-center justify-between gap-4 h-full">
                  <div className="flex items-center gap-4 min-w-0">
                    <div
                      className={`flex items-center justify-center h-10 w-10 shrink-0 rounded-full ${card.bg}`}
                    >
                      <img className="w-5 h-5" src="/images/seat.svg" alt="" />
                    </div>
                    <div className="min-w-0 text-left">
                      <p className="text-xs text-gray-500 font-light tracking-wide text-left">
                        {card.title}
                      </p>
                      <p className="mt-1 text-grease-100 text-xl font-bold text-left">
                        {card.count}
                      </p>
                    </div>
                  </div>
                  <i className="st-arrow-left rotate-180 text-xs text-royalblue-100 shrink-0"></i>
                </div>
              </Link>
            ))}
            <div className="bg-white rounded-xl sm:col-span-2 xl:col-span-3 p-4">
              <div className="flex items-center justify-between gap-4 mb-4">
                <p className="text-sm text-grease-100 font-semibold">Loads Trend</p>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <div className="bg-violet-100 h-3 w-3 rounded-full"></div>
                    <p className="text-gray-500 text-xs">Posted</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="bg-skyblue-100 h-3 w-3 rounded-full"></div>
                    <p className="text-gray-500 text-xs">Answered</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="bg-brown-100 h-3 w-3 rounded-full"></div>
                    <p className="text-gray-500 text-xs">Unanswered</p>
                  </div>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={monthlyData} barGap={4} margin={{ bottom: 12 }}>
                  <CartesianGrid
                    strokeDasharray="0"
                    horizontal={true}
                    vertical={false}
                    stroke="#F0F0F0"
                  />
                  <XAxis
                    dataKey="month"
                    axisLine={false}
                    tickLine={false}
                    interval={0}
                    angle={-45}
                    textAnchor="end"
                    height={48}
                    tickMargin={8}
                    tick={{ fill: '#A0AEC0', fontSize: 10 }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#A0AEC0', fontSize: 12 }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar
                    dataKey="Posted"
                    fill="rgb(95 112 228 / 1)"
                    radius={[10, 10, 10, 10]}
                    barSize={8}
                  />
                  <Bar
                    dataKey="Answered"
                    fill="rgb(95 210 228 / 1)"
                    radius={[10, 10, 10, 10]}
                    barSize={8}
                  />
                  <Bar
                    dataKey="Unanswered"
                    fill="rgb(228 157 95 / 1)"
                    radius={[10, 10, 10, 10]}
                    barSize={8}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="md:col-span-4 bg-white rounded-xl p-3">
            <div className="flex items-center justify-between mb-16">
              <p className="text-sm text-grease-100 font-semibold">Load Distribution</p>
            </div>
            <div className="flex justify-center items-center">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                  <Pie
                    data={loadDistributionData}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    animationBegin={0}
                    animationDuration={500} // Reduce animation duration for smoother experience
                    animationEasing="ease-out" // Use 'ease-out' for a smoother finish
                    isAnimationActive={true} // Disable animation to improve performance
                    label={renderCustomizedLabel}
                  >
                    {loadDistributionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>

            </div>
            <div className="flex flex-col items-center justify-center mt-2">
              <p className="text-grease-100 text-xl font-bold">
                {dashboardData?.loadPostedCount || 0}
              </p>
              <p className="text-gray-500 text-xs">Total Posted</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdminDashboard;
